import { Link, useParams } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { movies } from '@/mocks/movies';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((item) => item.id === id);

  usePageMeta(
    article ? `${article.title} - 影视资讯 - 青苹果影院` : '资讯不存在 - 青苹果影院',
    article ? `${article.summary} 发布时间：${article.date}，作者：${article.author}。` : '该资讯可能已被移除，返回资讯列表继续阅读。',
    article ? '影视资讯,影视观察,幕后故事,青苹果影院' : '青苹果影院',
  );

  if (!article) {
    return (
      <div className="w-full">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <EmptyState
            icon="ri-newspaper-line"
            title="资讯不存在"
            description="你访问的资讯可能已被移除，或者链接有误。可以返回资讯列表继续阅读。"
            actionLabel="返回资讯列表"
            actionTo="/news"
          />
        </div>
      </div>
    );
  }

  const related = newsList.filter((item) => item.id !== article.id).slice(0, 4);
  const hotMovies = [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 4);

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-11 flex items-center gap-2 text-xs text-foreground-500 overflow-hidden">
          <Link to="/" className="hover:text-primary-700 transition-colors whitespace-nowrap">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm shrink-0" aria-hidden="true" />
          <Link to="/news" className="hover:text-primary-700 transition-colors whitespace-nowrap">
            影视资讯
          </Link>
          <i className="ri-arrow-right-s-line text-sm shrink-0" aria-hidden="true" />
          <span className="text-foreground-800 truncate">{article.title}</span>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-8 md:pt-10 pb-14 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <article className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800">影视观察</span>
                <span className="font-label">{article.date}</span>
                <span>·</span>
                <span>{article.author}</span>
              </div>

              <h1 className="mt-4 text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 leading-[1.3] tracking-tight">
                {article.title}
              </h1>

              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{article.summary}</p>

              <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 影视资讯`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="mt-2 text-[11px] text-foreground-400">配图为本站演示用图，与内容无直接关联</p>

              <div className="mt-7">
                <p className="text-base md:text-lg text-foreground-800 leading-[1.95] font-medium">
                  {article.content[0]}
                </p>
                {article.content.slice(1).map((paragraph, index) => (
                  <p key={index} className="mt-5 text-sm md:text-base text-foreground-700 leading-[1.95]">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 rounded-md bg-background-100 border-l-2 border-primary-500 px-4 py-3.5">
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  本文为青苹果影院影视资讯栏目的演示内容，所涉影片、人物与数据均为虚构，仅用于展示资讯阅读页面。
                </p>
              </div>

              <div className="mt-7 pt-5 border-t border-background-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true" />
                  返回资讯列表
                </Link>
                <div className="flex items-center gap-2 text-xs text-foreground-400">
                  <i className="ri-time-line" aria-hidden="true" />
                  发布于 {article.date}
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="text-base font-semibold text-foreground-950">相关资讯</h2>
              <ul className="mt-3 divide-y divide-background-200">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex items-start gap-3 py-3">
                      <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary-300 shrink-0 group-hover:bg-accent-500 transition-colors" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-400 font-label">{item.date}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </article>

          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-foreground-950">热门影片</h2>
                <Link to="/category?tag=hot" className="text-xs text-primary-700 hover:text-primary-800">
                  全部
                </Link>
              </div>
              <ul className="mt-4 space-y-3">
                {hotMovies.map((movie, index) => (
                  <li key={movie.id}>
                    <Link to={`/detail/${movie.id}`} className="group flex items-center gap-3">
                      <span className="w-5 text-center font-label text-base font-semibold text-background-400 group-hover:text-primary-500 transition-colors">
                        {index + 1}
                      </span>
                      <span className="w-10 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={movie.poster}
                          alt={`${movie.title}海报`}
                          title={`${movie.title} - 影视详情`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                          {movie.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-500 truncate">
                          {movie.genres[0]} · {movie.year}
                        </span>
                      </span>
                      <span className="text-xs font-label font-semibold text-accent-700">{movie.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-accent-50 border border-accent-100 p-4 md:p-5">
              <div className="flex items-center gap-2 text-accent-800">
                <i className="ri-book-open-line text-lg" aria-hidden="true" />
                <span className="text-sm font-medium">继续阅读</span>
              </div>
              <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed">
                想找一部片？分类页支持类型、地区、年份多条件组合筛选。
              </p>
              <Link
                to="/category"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-accent-700 font-medium hover:text-accent-800 transition-colors"
              >
                去找影片
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}